<script lang="ts" setup>
import type { PropType } from "vue"
import type { GroupedFooterLinks, ICustomLink } from "./footer.interface"
import { computed } from "vue"
import { useTranslation } from "@/composables"
import { getBrandFooterUrls } from "./footer.constants"

const props = defineProps({
  customLinks: {
    type: Object as PropType<Array<ICustomLink> | null>,
    default: null,
  },
})

const { t } = useTranslation()

const hasCustomLinks = computed(() => props.customLinks && props.customLinks.length > 0)

const urls = getBrandFooterUrls()

const defaultGpxLinks: Array<GroupedFooterLinks> = [
  {
    header: t("footer.learn_more_title"),
    links: [
      { key: t("footer.host_login"), value: urls.hostLogin },
      { key: t("footer.get_guestpix"), value: urls.getStarted },
      { key: t("footer.faqs"), value: urls.faqs },
      { key: t("footer.pricing"), value: urls.pricing },
    ],
  },
  {
    header: t("footer.legal_title"),
    links: [
      { key: t("footer.privacy"), value: urls.privacy },
      { key: t("footer.terms_of_service"), value: urls.termsOfService },
      { key: t("footer.host_tcs"), value: urls.hostTcs },
      { key: t("footer.acceptable_use_policy"), value: urls.acceptableUsePolicy },
    ],
  },
]
</script>

<template>
  <div v-if="hasCustomLinks" class="gpx-footer-links_custom">
    <div v-for="(group, index) in customLinks" :key="index">
      <h6 v-if="group && !group.value" :class="{ 'gpx-top-margin': index > 0 }">
        {{ group.key }}
      </h6>
      <a v-else-if="group && group.value" :href="group.value">{{ group.key }}</a>
    </div>
  </div>

  <div v-else class="flex flex-row flex-wrap md:flex-nowrap gap-6 md:gap-12 text-left items-center">
    <div v-for="(group, index) in defaultGpxLinks" :key="index">
      <h6 class="font-semibold mb-4 md:mb-6">
        {{ group.header }}
      </h6>
      <ul class="list-none p-0 m-0 flex flex-col gap-2 md:gap-4">
        <li v-for="(link, linkIndex) in group.links" :key="linkIndex" class="gpx-footer-link" data-test="footer-link">
          <a v-if="link && link.value" :href="link.value">{{ link.key }}</a>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@reference "../../assets/styles.css"

.gpx-footer-links_custom {
  @apply flex gap-4 content-center flex-col text-center;
}

h6 {
  @apply text-base text-theme-link whitespace-nowrap;
}

a {
  @apply whitespace-nowrap text-theme-link no-underline;
}

a:hover {
  @apply underline;
}

.gpx-top-margin {
  @apply mt-8;
}

@media (width >= 768px) {
  .gpx-footer-links_custom {
    @apply text-left;
  }
}
</style>
